// Configuration générale de l'application.
// Tout ce qui se modifie sans toucher au code est ici.
//
// Ces valeurs sont EXPORTÉES autant que posées sur `window`.
// Le passage sur `window` sert au code existant et à la console ; l'export sert
// aux modules qui les lisent dès leur chargement. Sans lui, un outil de
// regroupement est libre d'évaluer un écran avant la configuration — et l'écran
// trouve alors `undefined`. C'est exactement ce qui s'est produit à la première
// compilation.

// -----------------------------------------------------------------------------
// SERVICES — montée en charge (voir ARCHITECTURE.md)
// Chaque brique s'active seule et se désactive en remettant sa valeur à vide.
// Tant que rien n'est rempli, l'application se comporte exactement comme avant.
// -----------------------------------------------------------------------------
/* ⚠️ CET IMPORT EST UNE GARANTIE D'ORDRE, PAS UNE DÉPENDANCE.
   `firebase-config.js` pose `window.FIREBASE_CONFIG`, que `store.js` lit DÈS
   SON ÉVALUATION pour décider s'il parle à Firebase ou s'il retombe en mode
   local. `app.js` importait bien les deux dans le bon ordre — mais l'ordre des
   lignes d'un fichier ne survit pas à la compilation : Vite regroupe les
   modules en paquets, et le paquet qui contient `store.js` contient aussi
   celui-ci. Le 24 août 2026, le regroupement a changé et `store.js` s'est
   retrouvé évalué AVANT la configuration : le site en ligne a basculé en
   « Mode démonstration » pour tout le monde, sans qu'aucune erreur ne
   s'affiche — la configuration était pourtant juste, et présente dans la page.

   ⚠️ ON NE CORRIGE DONC PAS EN DÉPLAÇANT DES LIGNES : cela remarcherait
   aujourd'hui et casserait au prochain fichier ajouté. Écrit comme un import,
   l'ordre devient une règle du langage — un module est toujours évalué après
   ce qu'il importe, quel que soit le découpage en paquets.

   ⚠️ ET IL EST INOFFENSIF AU TÉLÉPHONE. L'application native a sa propre
   configuration (`pont/firebase.js`) ; ce fichier n'y fait que poser une
   valeur que personne ne lit. */
import "./firebase-config.js";

window.SERVICES = {
  // Base principale : "auto" choisit Supabase si rempli, sinon Firebase, sinon local.
  base: "auto",

  supabase: {
    url: "",        // https://xxxxx.supabase.co
    cleAnonyme: "", // clé « anon public » — sans danger côté navigateur, la RLS protège
    taillePage: 30, // pagination par curseur
  },

  // Étapes suivantes : l'API (Cloudflare Worker) détiendra ces secrets.
  api: "",          // https://api.koinonia.workers.dev
  r2: { actif: false },
  redis: { actif: false },
  meilisearch: { actif: false, hote: "", cleRecherche: "" },
};

window.APP = {
  nom: "Koinonia",

  // Adresse publique de l'application. Sert de point de retour aux e-mails de
  // confirmation. Elle est FIXE et non déduite de `location.origin` : dans
  // l'application Android, l'origine est `https://localhost`, que Firebase
  // refuse comme adresse de retour — et l'e-mail n'était alors jamais envoyé.
  // ⚠️ Bascule du 19 août 2026 — voir `firebase-config.js`. Cette adresse sert
  // AUSSI de point de retour aux courriels : elle est donc soumise à la même
  // condition, le domaine doit être autorisé dans Firebase Auth.
  urlPublique: "https://koinonia.bj",

  /* Le serveur qui fait sonner les téléphones (Cloudflare Worker).
     Il ne détient aucune donnée : il vérifie qui appelle, lit les appareils du
     destinataire, et demande à Google de sonner. Son code vit dans
     `~/Desktop/koinonia-pousser`. Vider cette ligne éteint toutes les
     notifications poussées, sans rien casser d'autre. */
  urlPousser: "https://koinonia-pousser.icc-parakou.workers.dev",
  slogan: "Entre nous",
  // Baseline affichée sous le logo sur l'écran de connexion
  accroche: "Partagez votre foi, priez les uns pour les autres, restez connectés à la famille.",



  // Types de publication proposés dans le composeur
  typesPost: [
    { id: "post", label: "Publication", icone: "✍️", couleur: "brand" },
    { id: "priere", label: "Demande de prière", icone: "🙏", couleur: "prayer" },
    { id: "temoignage", label: "Témoignage", icone: "✨", couleur: "gold" },
    { id: "verset", label: "Verset", icone: "📖", couleur: "verse" },
  ],

  // Fonds proposés pour les statuts (stories en texte seul)
  fondsStory: [
    "linear-gradient(135deg,#1877f2,#0d4ea8)",
    "linear-gradient(135deg,#0d4ea8,#c9932a)",
    "linear-gradient(135deg,#0f766e,#22c55e)",
    "linear-gradient(135deg,#b45309,#f59e0b)",
    "linear-gradient(135deg,#1e3a8a,#3b82f6)",
    "linear-gradient(135deg,#9d174d,#f43f5e)",
    "linear-gradient(135deg,#1f2937,#4b5563)",
  ],

  // Taille maximale des vidéos (Mo). En mode démo local, le navigateur ne tient
  // qu'environ 5 Mo au total : on reste donc très bas.
  videoMaxMo: { local: 2, firebase: 60 },

  /* Les comptes d'accueil — ceux auxquels un nouveau venu sans ami est abonné
     à sa première ouverture, pour que son fil ne soit pas vide.
     Voir `js/decouverte.js`, qui explique pourquoi le seul compte du fondateur
     apporte AUSSI les publications de la page KOINONIA (il en est l'auteur, et
     une publication de page est toujours ouverte à tous).
     On y ajoute l'identifiant de toute autre personne qui écrirait au nom de
     la page : c'est l'auteur qui compte, pas la page. */
  decouverte: {
    membres: ["21hsQKogeAVKTlSuH83WgnMdzME3"], // Josaphat Kinsi, fondateur
    /* Les PAGES d'accueil, et c'est la voie la plus sûre des deux : leurs
       publications entrent dans le fil de tout membre connecté, sans
       abonnement et sans réglage à activer. Une page se lit parce que c'est
       une page — le serveur le dit déjà, et le banc l'éprouve (« une
       publication de page se lit sans connaître son auteur »).
       Celle-ci ne peut donc pas retomber en panne pour une case décochée. */
    pages: ["yxmB8WpXHiH1wWl2QSql"], // KOINONIA
  },

  // Motifs de signalement proposés
  motifsSignalement: [
    "Contenu contraire à la foi chrétienne",
    "Propos haineux ou insultants",
    "Contenu à caractère sexuel",
    "Arnaque, spam ou publicité",
    "Fausse information",
    "Autre",
  ],

  // -------------------------------------------------------------------------
  // POLITIQUE DE SPONSORING
  // Modifiable ici sans toucher au code : l'écran #/sponsoring lit ce contenu.
  // Version de référence et raisons de chaque règle : SPONSORING.md
  // -------------------------------------------------------------------------
  sponsoring: {
    version: "1.0",
    dateEffet: "2026-08-07",
    esprit:
      "Koinonia est un lieu de communion, pas une place de marché. Une mise en avant n'est " +
      "acceptée que si elle sert l'édification de la communauté : annoncer, rassembler, servir. " +
      "Rien ne s'achète ici qui touche à la foi, à la prière ou à la bénédiction.",
    sections: [
      {
        titre: "Qui peut sponsoriser",
        icone: "eglise",
        regles: [
          "Seules les **pages** peuvent demander une mise en avant : église, ministère, chorale, association.",
          "Un membre à titre personnel ne peut pas sponsoriser.",
          "La page doit être identifiable : nom réel, description claire, et au moins un administrateur joignable.",
          "L'administrateur qui fait la demande en répond personnellement devant la modération.",
        ],
      },
      {
        titre: "Ce qui peut être mis en avant",
        icone: "verset",
        regles: [
          "Un culte, une veillée, une convention, une retraite.",
          "Un concert de louange, une sortie d'album, une œuvre artistique chrétienne.",
          "Une formation biblique, un séminaire, une école du dimanche.",
          "Une action caritative : collecte pour un malade, aide à un sinistré, orphelinat, cantine.",
          "L'annonce d'une association chrétienne : recrutement de bénévoles, appel aux dons transparent.",
        ],
      },
      {
        titre: "Ce qui est refusé, sans exception",
        icone: "danger",
        regles: [
          "**Toute promesse de bénédiction, de guérison, de déblocage ou de miracle en échange d'argent** — semence, offrande conditionnelle, don « pour recevoir ».",
          "La vente d'objets présentés comme puissants : eau bénite, huiles, bracelets, savons, mouchoirs de délivrance.",
          "Toute promesse de guérison ou d'arrêt d'un traitement médical.",
          "Les campagnes politiques, les candidats, les partis.",
          "L'enrichissement rapide : placements, cryptomonnaies, vente pyramidale, prêts d'argent.",
          "Les jeux d'argent, l'alcool, le tabac, les rencontres amoureuses.",
          "Tout contenu contraire à la foi chrétienne, ou qui vise une personne ou une église nommément.",
          "Toute publicité s'adressant aux enfants.",
        ],
      },
      {
        titre: "La forme",
        icone: "commenter",
        regles: [
          "Une publication mise en avant porte toujours la mention **« Sponsorisé »**, visible sans effort.",
          "Elle est publiée au nom de la page, jamais sous le nom d'une personne.",
          "Elle ne doit pas imiter une publication ordinaire, un témoignage ou un message privé.",
          "Le texte reste vrai : pas de chiffre inventé, pas de témoignage fabriqué, pas de photo trompeuse.",
        ],
      },
      {
        titre: "La place dans le fil",
        icone: "fil",
        regles: [
          "Au plus une publication sponsorisée toutes les dix publications du fil.",
          "Une page ne peut pas avoir plus d'une mise en avant active à la fois.",
          "Une mise en avant dure au plus quatorze jours.",
          "Le fil reste d'abord celui de la communauté : les publications des membres passent avant.",
        ],
      },
      {
        titre: "L'examen et l'arrêt",
        icone: "options",
        regles: [
          "Chaque demande est examinée par la modération **avant** diffusion.",
          "Un refus est motivé et notifié à l'administrateur de la page.",
          "La modération peut arrêter une mise en avant à tout moment, même déjà commencée, si elle s'avère contraire à ces règles.",
          "Trois refus dans l'année retirent à la page le droit de sponsoriser.",
        ],
      },
      {
        titre: "L'argent",
        icone: "membres",
        regles: [
          "Aucun paiement ne se fait dans l'application : tout se règle en dehors, avec les responsables de Koinonia.",
          "Le tarif est le même pour tous, et connu d'avance.",
          "Payer n'achète ni un avis favorable, ni la levée d'une sanction, ni la mise en avant d'une doctrine.",
          "Une somme versée pour une mise en avant refusée avant diffusion est rendue.",
        ],
      },
      {
        titre: "Recours",
        icone: "messages",
        regles: [
          "Un refus peut être contesté en écrivant à la modération depuis l'application.",
          "La décision est réexaminée par un responsable qui n'a pas pris la première.",
          "Ces règles peuvent évoluer ; toute modification est annoncée quinze jours avant son application.",
        ],
      },
    ],
  },

  // Charte affichée à l'inscription
  // Délai que la modération s'engage à tenir pour examiner une attestation.
  // Affiché au membre à l'inscription et dans le bandeau d'attente : promettre
  // un délai qu'on affiche vaut mieux qu'un silence qui ressemble à une panne.
  delaiValidationHeures: 24,

  charte: [
    "Je m'exprime avec amour et respect (Éphésiens 4:29).",
    "Je ne publie rien de vulgaire, haineux ou trompeur.",
    "Je ne fais pas de ce lieu un espace de commerce ou de polémique.",
    "Je prie pour les autres membres au lieu de les juger.",
  ],

  // Versets du jour : le verset affiché change chaque jour (rotation sur la liste)
  versets: [
    { t: "Car là où deux ou trois sont assemblés en mon nom, je suis au milieu d'eux.", r: "Matthieu 18:20" },
    { t: "Je puis tout par celui qui me fortifie.", r: "Philippiens 4:13" },
    { t: "L'Éternel est mon berger : je ne manquerai de rien.", r: "Psaume 23:1" },
    { t: "Ta parole est une lampe à mes pieds, et une lumière sur mon sentier.", r: "Psaume 119:105" },
    { t: "Confie-toi en l'Éternel de tout ton cœur, et ne t'appuie pas sur ta sagesse.", r: "Proverbes 3:5" },
    { t: "Toutes choses concourent au bien de ceux qui aiment Dieu.", r: "Romains 8:28" },
    { t: "Ne crains rien, car je suis avec toi ; ne promène pas des regards inquiets.", r: "Ésaïe 41:10" },
    { t: "Que tout ce qui respire loue l'Éternel !", r: "Psaume 150:6" },
    { t: "Aimez-vous les uns les autres comme je vous ai aimés.", r: "Jean 13:34" },
    { t: "Venez à moi, vous tous qui êtes fatigués et chargés, et je vous donnerai du repos.", r: "Matthieu 11:28" },
    { t: "Le fruit de l'Esprit, c'est l'amour, la joie, la paix, la patience…", r: "Galates 5:22" },
    { t: "Cherchez premièrement le royaume et la justice de Dieu.", r: "Matthieu 6:33" },
    { t: "Ceux qui se confient en l'Éternel renouvellent leur force.", r: "Ésaïe 40:31" },
    { t: "Ma grâce te suffit, car ma puissance s'accomplit dans la faiblesse.", r: "2 Corinthiens 12:9" },
    { t: "Priez sans cesse, rendez grâces en toutes choses.", r: "1 Thessaloniciens 5:17-18" },
    { t: "Dieu est amour ; et celui qui demeure dans l'amour demeure en Dieu.", r: "1 Jean 4:16" },
    { t: "Je lève mes yeux vers les montagnes : d'où me viendra le secours ?", r: "Psaume 121:1" },
    { t: "Soyez toujours joyeux dans le Seigneur.", r: "Philippiens 4:4" },
    { t: "Car je connais les projets que j'ai formés sur vous, projets de paix.", r: "Jérémie 29:11" },
    { t: "Le Seigneur est fidèle, il vous affermira et vous préservera du malin.", r: "2 Thessaloniciens 3:3" },
    { t: "Heureux ceux qui procurent la paix, car ils seront appelés fils de Dieu.", r: "Matthieu 5:9" },
    { t: "Vous êtes la lumière du monde.", r: "Matthieu 5:14" },
    { t: "L'Éternel combattra pour vous ; et vous, gardez le silence.", r: "Exode 14:14" },
    { t: "Recommande ton sort à l'Éternel, mets en lui ta confiance, et il agira.", r: "Psaume 37:5" },
    { t: "Si quelqu'un est en Christ, il est une nouvelle créature.", r: "2 Corinthiens 5:17" },
    { t: "Que votre parole soit toujours accompagnée de grâce.", r: "Colossiens 4:6" },
    { t: "Portez les fardeaux les uns des autres.", r: "Galates 6:2" },
    { t: "Goûtez et voyez combien l'Éternel est bon !", r: "Psaume 34:8" },
    { t: "Rien n'est impossible à Dieu.", r: "Luc 1:37" },
    { t: "Le juste vivra par la foi.", r: "Romains 1:17" },
    { t: "Que la paix de Christ règne dans vos cœurs.", r: "Colossiens 3:15" },
  ],
};

// Ce que les modules importent explicitement, plutôt que d'espérer un ordre.
export const APP = window.APP;
export const SERVICES = window.SERVICES;
